
/**
 * Replaces DynamicTable from common-lib, including some default templates for sorting and toggling
 *
 * MUST be imported into Vue as "DynamicTable" to effectively replace the base implementation
 */
import { Component, Vue, Prop } from 'vue-property-decorator';
import DynamicTable from 'ah-common-lib/src/common/components/listing/DynamicTable.vue';
import { ListResponse } from 'ah-api-gateways';
import { RequestState } from 'ah-requests';
import { TableFieldDefinition } from 'ah-common-lib/src/models';
@Component({
  components: {
    DynamicTable,
  },
})
export default class XHedgeDynamicTable<T = any> extends Vue {
  @Prop({ default: null }) data!: ListResponse<T> | null;

  @Prop({ default: 'idle' }) dataLoadState!: RequestState;

  @Prop({ default: () => [] }) selectedItems!: any[];

  @Prop({ default: false }) withRowDetails!: boolean | string | Function;

  @Prop({ default: false }) withRowSubView!: boolean | string | Function;

  @Prop({ default: false }) animateCols!: boolean | string;

  @Prop({ default: true }) flush!: boolean | string;

  @Prop({ required: false }) rowClass!: string | Function;

  @Prop({ default: 'items' }) itemsLabel!: string;

  @Prop({ default: 'id' }) primaryKey!: string;

  /**
   * Breakpoint at which the table converts to a staked view
   *
   * Also affects the application of the `table-stacked` class
   */
  @Prop({ default: '900px' }) breakpoint!: string;

  /**
   * Whether to use flex scroll
   *
   * Flex scroll requires setting 'flex-basis' values for every column
   * Please refer to PrimeVue documentation
   */
  @Prop({ default: false }) withFlexScroll!: string | boolean;

  /**
   * Whether to use 'simple' scroll - overriden by `withFlexScroll`
   *
   * In this mode, fixed columns are not possible (will simply add a overflow: auto to the wrapping element)
   */
  @Prop({ default: false }) withSimpleScroll!: string | boolean;

  private expandedRows: any[] = [];

  get tableFields() {
    const fields: TableFieldDefinition[] = [...((this.$attrs.fields as any) || [])];

    if (this.withRowDetails !== false || this.withRowSubView !== false) {
      fields.push({
        header: '',
        key: 'xHedgeSelectableToggle',
        class: 'toggle-col',
        sortable: false,
      });
    }

    return fields;
  }

  xHedgeRowClass(item: any, type: any) {
    const classes = [];
    if (this.rowClass) {
      classes.push(typeof this.rowClass === 'function' ? this.rowClass(item, type) : this.rowClass);
    }

    if (this.selectedItems.includes(item[this.primaryKey])) {
      classes.push('selected-row');
    }

    if (this.hasDetailsOpen(item)) {
      classes.push('b-table-has-details');
    }

    if (this.hasRowDetails(item)) {
      classes.push('with-details');
    }

    if (this.hasRowSubView(item)) {
      classes.push('with-subview');
    }

    return classes.join(' ');
  }

  hasDetailsOpen(item: any) {
    return item[this.primaryKey] && this.expandedRows.includes(item[this.primaryKey]);
  }

  hasRowDetails(item: any) {
    return typeof this.withRowDetails === 'function' ? this.withRowDetails(item) : this.withRowDetails !== false;
  }

  hasRowSubView(item: any) {
    return typeof this.withRowSubView === 'function' ? this.withRowSubView(item) : this.withRowSubView !== false;
  }
}
